import { useMemo, useState } from "react";
import {
  ConfigProvider,
  DatePicker,
  Flex,
  PaginationProps,
  Table as AntdTable,
  Typography,
} from "antd";
import { Dayjs } from "dayjs";
import { useGetSales, useGetSalesOptions } from "./hooks/useSales";
import { getColumns, getExpandedRowColumns } from "./utils/table";
import { DEFAULT_PAGINATION } from "@/constants/table";
import ImportCsv from "./components/ImportCsv";
import { SortCondition } from "@/components/table/hooks/useSort";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import Table, { FilterOptions } from "@/components/table";
import { formatCurrency } from "@/utils/currency";

const SummaryCell = AntdTable.Summary.Cell;
const SummaryRow = AntdTable.Summary.Row;

const SalesOverview = () => {
  const [sort, setSort] = useState<SortCondition | undefined>({
    field: "sale_date",
    order: "desc",
  });
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>();

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>();
  const startDate = dateRange?.[0]?.startOf("day").toISOString();
  const endDate = dateRange?.[1]?.endOf("day").toISOString();

  const { data, isFetching, refetch } = useGetSales({
    params: {
      sale_date_between: dateRange ? `${startDate},${endDate}` : undefined,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetSalesOptions({
    params: {
      only: "invoice_no,payment_type",
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      invoice_no: (options || []).reduce((acc, item) => {
        if (
          item.invoice_no &&
          !acc.some((option) => option.value === item?.invoice_no)
        ) {
          acc.push({ text: item.invoice_no, value: item.invoice_no });
        }
        return acc;
      }, [] as FilterOptions[]),
      payment_type: (options || []).reduce((acc, item) => {
        if (
          item.payment_type &&
          !acc.some((option) => option.value === item?.payment_type)
        ) {
          acc.push({
            text: item.payment_type.replace("_", " ").toUpperCase(),
            value: item.payment_type,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  return (
    <>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Sales Overview
      </Typography.Title>

      <Flex vertical={true} gap={16}>
        <Flex vertical={false} justify="space-between" align="center" gap={16}>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
          />

          <ImportCsv onImportSuccess={() => refetch()} />
        </Flex>

        <Table
          columns={(props) =>
            getColumns({
              ...props,
              filterOptions,
            })
          }
          dataSource={data?.data || []}
          loading={isFetching}
          pagination={{
            defaultPagination: {
              ...DEFAULT_PAGINATION,
              showSizeChanger: true,
            },
            dataPagination: data?.pagination,
            onChange: setPagination,
          }}
          expandable={{
            expandedRowOffset: 1,
            expandedRowClassName: "shift-schedule-expanded-row",
            expandRowByClick: true,
            expandedRowRender: (record) => (
              <Flex vertical gap={0} style={{ margin: 0, padding: 0 }}>
                <ConfigProvider
                  theme={{
                    inherit: false,
                    components: {
                      Table: {
                        margin: 0,
                        headerBg: "#ffffff",
                        headerBorderRadius: 0,
                        footerBg: "#ffffff",
                      },
                    },
                  }}
                >
                  <Table
                    columns={getExpandedRowColumns}
                    dataSource={record?.items || []}
                    style={{ left: 0 }}
                    scroll={{ x: "max-content" }}
                    summary={() => {
                      return (
                        <>
                          <SummaryRow style={{ backgroundColor: "#fafafa" }}>
                            <SummaryCell index={0} colSpan={3} align="right">
                              <Typography.Text strong>
                                Subtotal:
                              </Typography.Text>
                            </SummaryCell>
                            <SummaryCell index={1}>
                              <Typography.Text>
                                {formatCurrency(record.subtotal || 0)}
                              </Typography.Text>
                            </SummaryCell>
                          </SummaryRow>
                          <SummaryRow style={{ backgroundColor: "#fafafa" }}>
                            <SummaryCell index={0} colSpan={3} align="right">
                              <Typography.Text strong>Tax:</Typography.Text>
                            </SummaryCell>
                            <SummaryCell index={1}>
                              <Typography.Text>
                                {formatCurrency(record.tax || 0)}
                              </Typography.Text>
                            </SummaryCell>
                          </SummaryRow>
                          <SummaryRow style={{ backgroundColor: "#fafafa" }}>
                            <SummaryCell index={0} colSpan={3} align="right">
                              <Typography.Text strong>
                                Discount:
                              </Typography.Text>
                            </SummaryCell>
                            <SummaryCell index={1}>
                              <Typography.Text>
                                {formatCurrency(record.discount || 0)}
                              </Typography.Text>
                            </SummaryCell>
                          </SummaryRow>
                          <SummaryRow style={{ backgroundColor: "#fafafa" }}>
                            <SummaryCell index={0} colSpan={3} align="right">
                              <Typography.Text strong>Total:</Typography.Text>
                            </SummaryCell>
                            <SummaryCell index={1}>
                              <Typography.Text>
                                {formatCurrency(
                                  Number(record.subtotal || 0) +
                                    Number(record.tax || 0) -
                                    Number(record.discount || 0)
                                )}
                              </Typography.Text>
                            </SummaryCell>
                          </SummaryRow>
                        </>
                      );
                    }}
                  />
                </ConfigProvider>
              </Flex>
            ),
          }}
          onRow={() => ({
            style: {
              verticalAlign: "top",
            },
          })}
          scroll={{
            x: "max-content",
          }}
          initialSort={sort}
          onSort={setSort}
          onFilter={setFilters}
        />
      </Flex>
    </>
  );
};

export default SalesOverview;
